'use client';

import PropTypes from 'prop-types';
import Link from 'next/link';
import { Box, Typography } from '@mui/material';
import {
  MARKETING_PAIR_BLOCK_SX,
  MARKETING_PAIR_IMAGE_SX,
  MarketingNumberedBlock,
  MarketingStickyIndex,
} from '@/components/website/Marketing/MarketingIndexChrome';
import MarketingReveal from '@/components/website/Marketing/MarketingReveal';
import {
  MARKETING,
  MARKETING_BODY_SX,
  MARKETING_EYEBROW_SX,
  MARKETING_HEADING_SX,
  MARKETING_NUM_SX,
  MARKETING_SOLID_CTA_SX,
  MARKETING_TEXT_CTA_SX,
} from '@/components/website/Marketing/marketing-tokens';
import { BOOK_ONLINE_PATH } from '@/consts/routes';
import MarketingPageHero from './MarketingPageHero';
import MarketingSectionImage from './MarketingSectionImage';
import {
  ProcessApprovalsSection,
  ProcessBuildSection,
  ProcessTextImageSection,
  ProcessUniqueSection,
} from './MarketingPageProcessSections';
import {
  indexEntriesFromSections,
  resolvedImageAlt,
  resolvedImageUrl,
} from './marketing-page-utils';

function IntroQuoteSection({ section }) {
  const data = section.data || {};
  return (
    <MarketingReveal
      id={section.id}
      sx={{
        borderTop: `1px solid ${MARKETING.hairline}`,
        borderBottom: `1px solid ${MARKETING.hairline}`,
        px: MARKETING.padX,
        py: '76px',
      }}
    >
      <Typography
        sx={{
          m: 0,
          mx: 'auto',
          maxWidth: 1000,
          fontFamily: MARKETING.serif,
          fontWeight: 300,
          fontSize: { xs: 'clamp(28px, 6vw, 46px)', md: '46px' },
          lineHeight: 1.32,
          letterSpacing: '-0.015em',
          textWrap: 'pretty',
          color: MARKETING.muted,
        }}
      >
        {data.quote}
      </Typography>
      {data.attribution ? (
        <Typography
          sx={{
            mt: '24px',
            mx: 'auto',
            maxWidth: 1000,
            fontFamily: MARKETING.sans,
            fontSize: '13px',
            letterSpacing: '0.12em',
            textTransform: 'uppercase',
            color: MARKETING.dim,
          }}
        >
          {data.attribution}
        </Typography>
      ) : null}
    </MarketingReveal>
  );
}

IntroQuoteSection.propTypes = { section: PropTypes.object.isRequired };

function IndexedTextImageSection({ section }) {
  const data = section.data || {};
  const imageUrl = resolvedImageUrl(data.imageAssetId);
  const imageAlt = resolvedImageAlt(data.imageAssetId, data.imageAlt || data.heading || '');
  const imageOnLeft = data.imageSide === 'left';

  return (
    <MarketingNumberedBlock
      id={data.indexId || section.id}
      num={data.indexNum || ''}
      contentSx={MARKETING_PAIR_BLOCK_SX}
      stagger
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: '18px',
          order: { md: imageOnLeft ? 2 : 1 },
        }}
      >
        {data.eyebrow ? (
          <MarketingReveal observe={false} delay={0}>
            <Typography sx={MARKETING_EYEBROW_SX}>{data.eyebrow}</Typography>
          </MarketingReveal>
        ) : null}
        <MarketingReveal observe={false} delay={0.15}>
          <Typography component="h2" sx={MARKETING_HEADING_SX}>
            {data.heading}
          </Typography>
        </MarketingReveal>
        {data.body ? (
          <MarketingReveal observe={false} delay={0.3}>
            <Typography sx={{ ...MARKETING_BODY_SX, whiteSpace: 'pre-line' }}>
              {data.body}
            </Typography>
          </MarketingReveal>
        ) : null}
      </Box>
      {imageUrl ? (
        <MarketingReveal
          observe={false}
          delay={0.5}
          from={imageOnLeft ? 'left' : 'right'}
          sx={{ width: '100%', order: { xs: -1, md: imageOnLeft ? 1 : 2 } }}
        >
          <MarketingSectionImage
            src={imageUrl}
            alt={imageAlt}
            sizes="(max-width: 899px) 100vw, 50vw"
            sx={MARKETING_PAIR_IMAGE_SX}
          />
        </MarketingReveal>
      ) : null}
    </MarketingNumberedBlock>
  );
}

IndexedTextImageSection.propTypes = { section: PropTypes.object.isRequired };

function CraftsmanshipGallerySection({ section }) {
  const data = section.data || {};
  const images = Array.isArray(data.images) ? data.images : [];
  const directions = ['left', 'right', 'down', 'up'];

  return (
    <MarketingNumberedBlock
      id={data.indexId || section.id}
      num={data.indexNum || ''}
      stagger
      sx={{ pb: '96px' }}
      contentSx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', md: 'minmax(260px, 1fr) minmax(280px, 1.15fr)' },
        gap: { xs: '28px', md: '40px 48px' },
        alignItems: 'start',
      }}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
        {data.eyebrow ? (
          <MarketingReveal observe={false} delay={0}>
            <Typography sx={MARKETING_EYEBROW_SX}>{data.eyebrow}</Typography>
          </MarketingReveal>
        ) : null}
        <MarketingReveal observe={false} delay={0.15}>
          <Typography
            component="h2"
            sx={{
              ...MARKETING_HEADING_SX,
              fontSize: { xs: 'clamp(32px, 8vw, 56px)', md: '56px' },
              lineHeight: 1.06,
            }}
          >
            {data.heading}
          </Typography>
        </MarketingReveal>
        {data.body ? (
          <MarketingReveal observe={false} delay={0.3}>
            <Typography sx={{ ...MARKETING_BODY_SX, maxWidth: 'none', whiteSpace: 'pre-line' }}>
              {data.body}
            </Typography>
          </MarketingReveal>
        ) : null}
      </Box>
      <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
        {images.map((item, index) => {
          const src = resolvedImageUrl(item.imageAssetId);
          if (!src) return null;
          return (
            <MarketingReveal
              key={`${section.id}-img-${index}`}
              observe={false}
              from={directions[index % directions.length]}
              delay={0.55 + index * 0.15}
              duration={0.85}
              sx={{ width: '100%' }}
            >
              <MarketingSectionImage
                src={src}
                alt={resolvedImageAlt(item.imageAssetId, item.alt || '')}
                sizes="(max-width: 899px) 50vw, 25vw"
                sx={{ width: '100%', aspectRatio: '1' }}
              />
            </MarketingReveal>
          );
        })}
      </Box>
    </MarketingNumberedBlock>
  );
}

CraftsmanshipGallerySection.propTypes = { section: PropTypes.object.isRequired };

function ShowroomSection({ section }) {
  const data = section.data || {};
  const imageUrl = resolvedImageUrl(data.imageAssetId);
  const imageAlt = resolvedImageAlt(data.imageAssetId, data.imageAlt || data.heading || '');
  const hasAddress = Boolean(data.address);
  const hasHours = Boolean(data.hours);

  return (
    <MarketingNumberedBlock
      id={data.indexId || section.id}
      num={data.indexNum || ''}
      contentSx={MARKETING_PAIR_BLOCK_SX}
      stagger
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: '18px', order: { md: 1 } }}>
        {data.eyebrow ? (
          <MarketingReveal observe={false} delay={0}>
            <Typography sx={MARKETING_EYEBROW_SX}>{data.eyebrow}</Typography>
          </MarketingReveal>
        ) : null}
        <MarketingReveal observe={false} delay={0.15}>
          <Typography component="h2" sx={MARKETING_HEADING_SX}>
            {data.heading}
          </Typography>
        </MarketingReveal>
        {data.body ? (
          <MarketingReveal observe={false} delay={0.3}>
            <Typography sx={{ ...MARKETING_BODY_SX, whiteSpace: 'pre-line' }}>
              {data.body}
            </Typography>
          </MarketingReveal>
        ) : null}
        <MarketingReveal observe={false} delay={0.4}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '18px' }}>
            {hasAddress || hasHours ? (
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                  gap: '18px 32px',
                  maxWidth: 460,
                }}
              >
                {hasAddress ? (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: '7px' }}>
                    <Typography
                      sx={{
                        fontFamily: MARKETING.sans,
                        fontWeight: 400,
                        fontSize: '10px',
                        letterSpacing: '0.24em',
                        textTransform: 'uppercase',
                        color: MARKETING.dim,
                      }}
                    >
                      Address
                    </Typography>
                    <Typography
                      sx={{
                        fontFamily: MARKETING.sans,
                        fontWeight: 400,
                        fontSize: '14px',
                        lineHeight: 1.7,
                        whiteSpace: 'pre-line',
                      }}
                    >
                      {data.address}
                    </Typography>
                  </Box>
                ) : null}
                {hasHours ? (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: '7px' }}>
                    <Typography
                      sx={{
                        fontFamily: MARKETING.sans,
                        fontWeight: 400,
                        fontSize: '10px',
                        letterSpacing: '0.24em',
                        textTransform: 'uppercase',
                        color: MARKETING.dim,
                      }}
                    >
                      Hours
                    </Typography>
                    <Typography
                      sx={{
                        fontFamily: MARKETING.sans,
                        fontWeight: 400,
                        fontSize: '14px',
                        lineHeight: 1.7,
                        whiteSpace: 'pre-line',
                      }}
                    >
                      {data.hours}
                    </Typography>
                  </Box>
                ) : null}
              </Box>
            ) : null}
            <Box sx={{ display: 'flex', gap: '16px', alignItems: 'center', flexWrap: 'wrap' }}>
              {data.ctaLabel ? (
                <Box component={Link} href={BOOK_ONLINE_PATH} sx={MARKETING_SOLID_CTA_SX}>
                  {data.ctaLabel}
                </Box>
              ) : null}
              {data.mapsUrl ? (
                <Box
                  component="a"
                  href={data.mapsUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={MARKETING_TEXT_CTA_SX}
                >
                  Get Directions
                </Box>
              ) : null}
            </Box>
          </Box>
        </MarketingReveal>
      </Box>
      {imageUrl ? (
        <MarketingReveal
          observe={false}
          delay={0.5}
          from="right"
          sx={{ width: '100%', order: { xs: -1, md: 2 } }}
        >
          <MarketingSectionImage
            src={imageUrl}
            alt={imageAlt}
            sizes="(max-width: 899px) 100vw, 50vw"
            sx={{
              width: '100%',
              height: { xs: 240, sm: 300, md: 360 },
              boxShadow: '0 32px 64px rgba(0, 0, 0, 0.5)',
            }}
          />
        </MarketingReveal>
      ) : null}
    </MarketingNumberedBlock>
  );
}

ShowroomSection.propTypes = { section: PropTypes.object.isRequired };

function EnvironmentSection({ section }) {
  const data = section.data || {};
  const logos = Array.isArray(data.logos) ? data.logos : [];
  const bullets = Array.isArray(data.bullets) ? data.bullets.filter(Boolean) : [];
  const backgroundUrl = resolvedImageUrl(data.backgroundImageAssetId);
  const backgroundAlt = resolvedImageAlt(data.backgroundImageAssetId, '');

  return (
    <Box
      component="section"
      id={data.indexId || section.id}
      sx={{
        position: 'relative',
        overflow: 'hidden',
        bgcolor: '#0E1511',
        scrollMarginTop: '24px',
      }}
    >
      {backgroundUrl ? (
        <MarketingSectionImage
          src={backgroundUrl}
          alt={backgroundAlt}
          sizes="100vw"
          sx={{
            position: 'absolute',
            inset: 0,
            width: '100%',
            height: '100%',
          }}
        />
      ) : null}
      <Box
        aria-hidden
        sx={{
          position: 'absolute',
          inset: 0,
          background:
            'linear-gradient(180deg, rgba(10, 16, 12, 0.55) 0%, rgba(10, 16, 12, 0.34) 45%, rgba(10, 16, 12, 0.62) 100%)',
        }}
      />
      <MarketingReveal
        sx={{
          position: 'relative',
          px: MARKETING.padX,
          py: '120px',
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: '88px 1fr' },
          gap: '32px',
          alignItems: 'start',
          transition: `opacity 0.8s ${MARKETING.ease}, transform 0.8s ${MARKETING.ease}`,
        }}
      >
        {data.indexNum ? (
          <Typography
            sx={{
              ...MARKETING_NUM_SX,
              color: 'rgba(255, 255, 255, 0.3)',
              display: { xs: 'none', sm: 'block' },
            }}
          >
            {data.indexNum}
          </Typography>
        ) : (
          <Box sx={{ display: { xs: 'none', sm: 'block' } }} />
        )}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
            gap: { xs: '40px', md: '56px 48px' },
            alignItems: 'start',
          }}
        >
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '24px', maxWidth: 560 }}>
            {data.eyebrow ? (
              <Typography
                sx={{
                  fontFamily: MARKETING.sans,
                  fontWeight: 400,
                  fontSize: '10px',
                  letterSpacing: '0.32em',
                  textTransform: 'uppercase',
                  color: 'rgba(255, 255, 255, 0.62)',
                }}
              >
                {data.eyebrow}
              </Typography>
            ) : null}
            <Typography
              component="h2"
              sx={{
                m: 0,
                fontFamily: MARKETING.serif,
                fontWeight: 300,
                fontSize: { xs: 'clamp(32px, 7vw, 52px)', md: '52px' },
                lineHeight: 1.14,
                color: '#fff',
                textWrap: 'pretty',
              }}
            >
              {data.heading}
            </Typography>
            {data.body ? (
              <Typography
                sx={{
                  m: 0,
                  fontFamily: MARKETING.sans,
                  fontWeight: 400,
                  fontSize: '15.5px',
                  lineHeight: 1.85,
                  color: 'rgba(255, 255, 255, 0.86)',
                  textWrap: 'pretty',
                  whiteSpace: 'pre-line',
                }}
              >
                {data.body}
              </Typography>
            ) : null}
          </Box>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '34px' }}>
            {bullets.length > 0 ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                {bullets.map((bullet, index) => (
                  <Box
                    key={`${section.id}-bullet-${index}`}
                    sx={{ display: 'flex', gap: '16px', alignItems: 'flex-start' }}
                  >
                    <Box
                      aria-hidden
                      sx={{
                        flex: 'none',
                        width: 5,
                        height: 5,
                        borderRadius: '50%',
                        bgcolor: 'rgba(255, 255, 255, 0.75)',
                        mt: '10px',
                      }}
                    />
                    <Typography
                      sx={{
                        fontFamily: MARKETING.sans,
                        fontWeight: 400,
                        fontSize: '15.5px',
                        lineHeight: 1.7,
                        color: 'rgba(255, 255, 255, 0.94)',
                        textWrap: 'pretty',
                      }}
                    >
                      {bullet}
                    </Typography>
                  </Box>
                ))}
              </Box>
            ) : null}
            <Box sx={{ display: 'flex', flexWrap: { xs: 'wrap', sm: 'nowrap' }, gap: '16px' }}>
              {logos.map((logo, index) => {
                const src = resolvedImageUrl(logo.imageAssetId);
                if (!src) return null;
                return (
                  <Box
                    key={`${section.id}-logo-${index}`}
                    sx={{
                      flex: '1 1 0',
                      minWidth: 0,
                      aspectRatio: '1',
                      maxWidth: 148,
                      bgcolor: '#fff',
                      position: 'relative',
                      boxSizing: 'border-box',
                    }}
                  >
                    <MarketingSectionImage
                      src={src}
                      alt={resolvedImageAlt(logo.imageAssetId, logo.alt || '')}
                      sizes="(max-width: 899px) 25vw, 148px"
                      objectFit="contain"
                      sx={{ position: 'absolute', inset: '18px' }}
                    />
                  </Box>
                );
              })}
            </Box>
          </Box>
        </Box>
      </MarketingReveal>
    </Box>
  );
}

EnvironmentSection.propTypes = { section: PropTypes.object.isRequired };

function ConsultationCtaSection({ section }) {
  const data = section.data || {};
  const imageUrl = resolvedImageUrl(data.imageAssetId);

  return (
    <Box
      component="section"
      id={section.id}
      sx={{
        position: 'relative',
        height: 380,
        overflow: 'hidden',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        borderTop: `1px solid ${MARKETING.hairline}`,
      }}
    >
      {imageUrl ? (
        <MarketingSectionImage
          src={imageUrl}
          alt=""
          sizes="100vw"
          sx={{
            position: 'absolute',
            inset: 0,
            width: '100%',
            height: '100%',
            opacity: 0.3,
          }}
        />
      ) : null}
      <Box
        sx={{
          position: 'relative',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '26px',
          textAlign: 'center',
          px: MARKETING.padX,
        }}
      >
        <Typography
          component="h2"
          sx={{
            m: 0,
            fontFamily: MARKETING.serif,
            fontWeight: 300,
            fontSize: { xs: 'clamp(32px, 7vw, 52px)', md: '52px' },
            lineHeight: 1.12,
          }}
        >
          {data.heading}
        </Typography>
        {data.body ? (
          <Typography
            component="h4"
            sx={{
              m: 0,
              fontFamily: MARKETING.serif,
              fontWeight: 300,
              fontSize: { xs: 'clamp(24px, 7vw, 36px)', md: '36px' },
              lineHeight: 1.12,
            }}
          >
            {data.body}
          </Typography>
        ) : null}
        {data.ctaLabel && data.ctaUrl ? (
          <Box component={Link} href={data.ctaUrl} sx={{ ...MARKETING_SOLID_CTA_SX, px: '38px' }}>
            {data.ctaLabel}
          </Box>
        ) : null}
      </Box>
    </Box>
  );
}

ConsultationCtaSection.propTypes = { section: PropTypes.object.isRequired };

/** Shared Brand/Process section renderer — also used by TradePage for non-trade types. */
export function renderMarketingSection(section, scrollTargetId) {
  switch (section.type) {
    case 'hero':
      return (
        <MarketingPageHero key={section.id} data={section.data} scrollTargetId={scrollTargetId} />
      );
    case 'intro_quote':
      return <IntroQuoteSection key={section.id} section={section} />;
    case 'indexed_text_image':
      return <IndexedTextImageSection key={section.id} section={section} />;
    case 'craftsmanship_gallery':
      return <CraftsmanshipGallerySection key={section.id} section={section} />;
    case 'showroom':
      return <ShowroomSection key={section.id} section={section} />;
    case 'environment':
      return <EnvironmentSection key={section.id} section={section} />;
    case 'consultation_cta':
      return <ConsultationCtaSection key={section.id} section={section} />;
    case 'process_text_image':
      return <ProcessTextImageSection key={section.id} section={section} />;
    case 'process_build':
      return <ProcessBuildSection key={section.id} section={section} />;
    case 'process_approvals':
      return <ProcessApprovalsSection key={section.id} section={section} />;
    case 'process_unique':
      return <ProcessUniqueSection key={section.id} section={section} />;
    default:
      return null;
  }
}

function renderSection(section, scrollTargetId) {
  return renderMarketingSection(section, scrollTargetId);
}

export default function MarketingPageSections({ sections, activeId }) {
  const indexEntries = indexEntriesFromSections(sections);
  const hasIndexedBody = indexEntries.length > 0;
  const intro = (sections ?? []).find(section => section.type === 'intro_quote');
  const hero = (sections ?? []).find(section => section.type === 'hero');
  const rest = (sections ?? []).filter(
    section => section.type !== 'hero' && section.type !== 'intro_quote',
  );
  const bodySections = rest.filter(section => section.type !== 'consultation_cta');
  const consultation = rest.filter(section => section.type === 'consultation_cta');
  const scrollTargetId =
    intro?.id || indexEntries[0]?.id || bodySections[0]?.data?.indexId || bodySections[0]?.id;

  return (
    <>
      {hero ? renderSection(hero, scrollTargetId) : null}
      {intro ? renderSection(intro) : null}
      {hasIndexedBody ? (
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '300px 1fr' } }}>
          <MarketingStickyIndex sections={indexEntries} activeId={activeId} />
          <Box>{bodySections.map(section => renderSection(section))}</Box>
        </Box>
      ) : (
        bodySections.map(section => renderSection(section))
      )}
      {consultation.map(section => renderSection(section))}
    </>
  );
}

MarketingPageSections.propTypes = {
  sections: PropTypes.arrayOf(PropTypes.object),
  activeId: PropTypes.string,
};
